import axios from 'axios';
import { useAuthStore } from '../stores/authStore';

const api = axios.create({
  baseURL: '/api',
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,
});

// Request Interceptor - Token hinzufuegen
api.interceptors.request.use(
  (config) => {
    // Versuche Token aus Zustand zu holen
    let token = useAuthStore.getState().token;

    // Falls nicht im Store, versuche aus localStorage
    if (!token) {
      try {
        const stored = localStorage.getItem('gbr-auth');
        if (stored) {
          const parsed = JSON.parse(stored);
          token = parsed.state?.token;
        }
      } catch {
        // Ignore parse errors
      }
    }

    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response Interceptor - Error Handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // Check for token-related errors
    const errorCode = error.response?.data?.code;
    const status = error.response?.status;

    if (status === 401 && useAuthStore.getState().isAuthenticated) {
      // Token expired or invalid - always logout
      useAuthStore.getState().logout();
      window.location.href = '/login';
      return Promise.reject(new Error('Session expired'));
    }

    // Extrahiere die echte Fehlermeldung vom Backend
    let message = error.response?.data?.error || error.message || 'An error occurred';

    // Make token errors more user-friendly
    if (errorCode === 'TOKEN_EXPIRED' || message.toLowerCase().includes('token expired')) {
      message = 'Your session has expired. Please log in again.';
    } else if (errorCode === 'INVALID_TOKEN' || message.toLowerCase().includes('invalid token')) {
      message = 'Your session is invalid. Please log in again.';
    }

    const customError = new Error(message);
    return Promise.reject(customError);
  }
);

export default api;

// ============================================
// AUTH API
// ============================================

export const authAPI = {
  register: async (data: {
    name: string;
    email: string;
    password: string;
    marketplace?: string;
    affiliateCodeUsed?: string;
  }) => {
    const response = await api.post('/auth/register', data);
    return response.data;
  },

  login: async (data: { email: string; password: string }) => {
    const response = await api.post('/auth/login', data);
    return response.data;
  },

  getMe: async () => {
    const response = await api.get('/auth/me');
    return response.data;
  },

  forgotPassword: async (email: string) => {
    const response = await api.post('/auth/forgot-password', { email });
    return response.data;
  },

  resetPassword: async (token: string, password: string) => {
    const response = await api.patch(`/auth/reset-password/${token}`, { password });
    return response.data;
  },

  deleteAccount: async () => {
    const response = await api.delete('/auth/delete-account');
    return response.data;
  },

  updateNotifications: async (data: {
    newReview: boolean;
    weeklyDigest: boolean;
    marketing: boolean;
  }) => {
    const response = await api.patch('/auth/notifications', { emailNotifications: data });
    return response.data;
  },
};

// ============================================
// BOOKS API
// ============================================

export const booksAPI = {
  // ASIN Lookup (Vorschau ohne Erstellen)
  lookupASIN: async (asin: string, marketplace: string = 'de') => {
    const response = await api.get(`/books/lookup/${asin}`, { params: { marketplace } });
    return response.data;
  },

  // Buch erstellen
  create: async (data: { asin: string; [key: string]: any }) => {
    const response = await api.post('/books', data);
    return response.data;
  },

  // Eigene Bücher
  getMyBooks: async () => {
    const response = await api.get('/books/my');
    return response.data?.data || [];
  },

  // Library (Bücher zum Reviewen)
  getLibrary: async (params?: {
    page?: number;
    limit?: number;
    category?: string;
    language?: string;
  }) => {
    const response = await api.get('/books/library', { params });
    return response.data?.data || [];
  },

  // Einzelnes Buch
  getBook: async (id: string) => {
    const response = await api.get(`/books/${id}`);
    return response.data?.data || response.data;
  },

  // Einzelnes Buch (alias)
  getById: async (id: string) => {
    const response = await api.get(`/books/${id}`);
    return response.data?.data || response.data;
  },

  // Buch aktualisieren
  update: async (id: string, data: Record<string, any>) => {
    const response = await api.patch(`/books/${id}`, data);
    return response.data;
  },

  // Buch löschen
  delete: async (id: string) => {
    const response = await api.delete(`/books/${id}`);
    return response.data;
  },

  // Bücher die ich reviewe
  getBooksImReviewing: async () => {
    const response = await api.get('/books/reviewing');
    return response.data?.data || [];
  },

  // Kampagne starten
  launchCampaign: async (
    bookId: string,
    data: {
      option: string;
      ebookPrice?: number;
      printPrice?: number;
      addPhoto?: boolean;
      addVideo?: boolean;
      maxReviewers?: number;
    }
  ) => {
    const response = await api.post(`/books/${bookId}/launch`, data);
    return response.data;
  },

  // Cancel free slots from a campaign
  cancelCampaign: async (bookId: string, campaignId: string, slotsToCancel?: number) => {
    const response = await api.delete(`/books/${bookId}/campaigns/${campaignId}`, {
      data: slotsToCancel ? { slotsToCancel } : undefined,
    });
    return response.data;
  },

  // Bulk cancel free slots across multiple campaigns
  bulkCancelSlots: async (bookId: string, cancellations: Array<{ campaignId: string; slotsToCancel: number }>) => {
    const response = await api.post(`/books/${bookId}/cancel-slots`, { cancellations });
    return response.data;
  },

  // Buch zum Reviewen auswählen
  pick: async (bookId: string, campaignId: string) => {
    const response = await api.patch(`/books/${bookId}/pick`, { campaignId });
    return response.data;
  },

  // Review abschließen
  finishReview: async (
    bookId: string,
    data: {
      rating: number;
      amazonName: string;
      postedWebsite?: string;
      reviewText: string;
    }
  ) => {
    const response = await api.patch(`/books/${bookId}/finish`, data);
    return response.data;
  },

  // Review abbrechen (Gems werden abgezogen)
  cancelReview: async (bookId: string) => {
    const response = await api.patch(`/books/${bookId}/cancel-review`);
    return response.data;
  },

  // Fertiges Review löschen (Credits und Gems werden abgezogen)
  deleteReview: async (bookId: string) => {
    const response = await api.delete(`/books/${bookId}/review`);
    return response.data;
  },

  // Fertiges Review bearbeiten
  updateReview: async (
    bookId: string,
    data: {
      rating?: number;
      amazonName?: string;
      postedWebsite?: string;
      reviewText?: string;
    }
  ) => {
    const response = await api.patch(`/books/${bookId}/review`, data);
    return response.data;
  },

  // Beschwerde über Review einreichen
  submitComplaint: async (
    bookId: string,
    data: {
      assignmentId: string;
      reason: 'no_amazon_review' | 'unfair_rating' | 'offensive_text' | 'spam' | 'other';
      description: string;
    }
  ) => {
    const response = await api.post(`/books/${bookId}/complaint`, data);
    return response.data;
  },

  // Beschwerde über Kampagne einreichen
  submitCampaignComplaint: async (
    bookId: string,
    data: {
      campaignId: string;
      reason: 'wrong_reward' | 'price_mismatch' | 'broken_link' | 'other';
      description: string;
    }
  ) => {
    const response = await api.post(`/books/${bookId}/campaign-complaint`, data);
    return response.data;
  },

  // Admin: Kampagne stoppen und Autor benachrichtigen
  adminStopCampaign: async (data: {
    complaintId: string;
    bookId: string;
    campaignId: string;
    emailSubject: string;
    emailBody: string;
  }) => {
    const response = await api.post('/books/admin/stop-campaign', data);
    return response.data;
  },

  // Beschwerden für ein Buch abrufen
  getBookComplaints: async (bookId: string) => {
    const response = await api.get(`/books/${bookId}/complaints`);
    return response.data;
  },

  // Admin: Alle Complaints abrufen
  getAllComplaints: async (status?: string) => {
    const params = status && status !== 'all' ? `?status=${status}` : '';
    const response = await api.get(`/books/admin/complaints${params}`);
    return response.data;
  },

  // Admin: Complaint Status updaten
  updateComplaint: async (
    complaintId: string,
    data: { status: string; adminNotes?: string }
  ) => {
    const response = await api.patch(`/books/admin/complaints/${complaintId}`, data);
    return response.data;
  },

  // Pending Books Count (für Admin Badge)
  getPendingCount: async () => {
    const response = await api.get('/books/admin/pending-count');
    return response.data?.count || 0;
  },

  // Advertisement: Get featured books (public)
  getFeaturedBooks: async () => {
    const response = await api.get('/books/ads/featured');
    return response.data?.data || [];
  },

  // Advertisement: Get my ad slots
  getMyAdSlots: async () => {
    const response = await api.get('/books/ads/my-slots');
    return response.data?.data || [];
  },

  // Advertisement: Purchase ad slots
  purchaseAdSlots: async (bookId: string, dates: string[]) => {
    const response = await api.post('/books/ads/purchase', { bookId, dates });
    return response.data;
  },

  // Advertisement: Cancel ad slot
  cancelAdSlot: async (slotId: string) => {
    const response = await api.delete(`/books/ads/${slotId}`);
    return response.data;
  },

  // Advertisement: PayPal config (public, client ID + price)
  getAdPaypalConfig: async () => {
    const response = await api.get('/books/ads/paypal/config');
    return response.data?.data || { configured: false };
  },

  // Advertisement: create a PayPal order to pay cash for ad slots
  createAdPaypalOrder: async (bookId: string, dates: string[]) => {
    const response = await api.post('/books/ads/paypal/create-order', { bookId, dates });
    return response.data?.data;
  },

  // Advertisement: capture a PayPal order after the author approved it
  captureAdPaypalOrder: async (orderId: string) => {
    const response = await api.post('/books/ads/paypal/capture-order', { orderId });
    return response.data;
  },

  // Boost a book with credits and/or gems
  boostBook: async (bookId: string, data: { credits?: number; gems?: number }) => {
    const response = await api.post(`/books/${bookId}/boost`, data);
    return response.data;
  },
};

// ============================================
// UPLOAD API
// ============================================

export const uploadAPI = {
  // Cover-Bild hochladen
  uploadCover: async (file: File) => {
    const formData = new FormData();
    formData.append('cover', file);
    
    const response = await api.post('/upload/cover', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  // Book file hochladen (EPUB, PDF, MOBI)
  uploadBook: async (file: File) => {
    const formData = new FormData();
    formData.append('book', file);
    
    const response = await api.post('/upload/book', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },
};
